import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../api/apiClient";
import type { Location } from "../api/types";
import { ScheduleWorkspace } from "../components/ScheduleWorkspace";
import { VacationApprovalsCard } from "../components/VacationApprovalsCard";
import { useAuth } from "../state/AuthContext";

export function SchedulePage() {
  const { user, hasRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const ownerScoped = hasRole("OWNER");
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(ownerScoped);

  const refreshLocations = useCallback(async () => {
    if (!ownerScoped) return;
    setLocationsLoading(true);
    try {
      const list = await api.listLocations();
      setLocations(list.filter((l) => l.isActive));
    } catch {
      setLocations([]);
    } finally {
      setLocationsLoading(false);
    }
  }, [ownerScoped]);

  useEffect(() => {
    void refreshLocations();
  }, [refreshLocations]);

  useEffect(() => {
    if (!ownerScoped || locations.length === 0) return;
    const q = searchParams.get("locationId");
    if (q && locations.some((l) => l.id === q)) return;
    setSearchParams({ locationId: locations[0].id }, { replace: true });
  }, [ownerScoped, locations, searchParams, setSearchParams]);

  const resolvedLocationId = useMemo(() => {
    if (ownerScoped) {
      const q = searchParams.get("locationId");
      if (q && locations.some((l) => l.id === q)) return q;
      return locations[0]?.id ?? "";
    }
    return user?.assignedLocation?.id ?? "";
  }, [ownerScoped, searchParams, locations, user?.assignedLocation?.id]);

  const shopPicker =
    ownerScoped && locations.length > 0 ? (
      <div className="field" style={{ maxWidth: "22rem" }}>
        <label htmlFor="schedule-shop-picker">Shop</label>
        <select
          id="schedule-shop-picker"
          value={resolvedLocationId}
          onChange={(e) =>
            setSearchParams({ locationId: e.target.value }, { replace: true })
          }
        >
          {locations.map((loc) => (
            <option key={loc.id} value={loc.id}>
              {loc.name} ({loc.code})
            </option>
          ))}
        </select>
      </div>
    ) : null;

  if (!user) {
    return null;
  }

  if (ownerScoped && locationsLoading) {
    return (
      <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <div className="empty-state">Loading shops…</div>
      </section>
    );
  }

  if (ownerScoped && locations.length === 0) {
    return (
      <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <h2 className="page-title">Schedule</h2>
        <p className="page-subtitle">Add an active shop before planning shifts.</p>
      </section>
    );
  }

  if (!resolvedLocationId) {
    return (
      <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <h2 className="page-title">Schedule</h2>
        <div className="alert error">
          You are not assigned to a shop. Ask an owner to assign you so you can view this
          calendar.
        </div>
      </section>
    );
  }

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      {hasRole("OWNER", "ADMIN", "MANAGER") ? <VacationApprovalsCard /> : null}
      <ScheduleWorkspace
        locationId={resolvedLocationId}
        headerExtra={shopPicker}
        backLink={{ to: "/app", label: "← Back to home" }}
      />
    </section>
  );
}
